import React, { useState, useEffect, useRef } from 'react';
import { Play, User } from 'lucide-react';
import UhcLogo from '../icons/UhcLogo';
import FeedbackButtons from './FeedbackButtons';

const BotAvatar = () => (
  <div style={{
    width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
    marginTop: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#fff', padding: 6,
    border: '1.5px solid #e0e3ea',
    boxShadow: '0 2px 10px rgba(0,38,119,0.10)',
  }}>
    <UhcLogo size={28} />
  </div>
);

const UserAvatar = ({ isDark }) => (
  <div style={{
    width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
    marginTop: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: isDark ? '#374151' : '#e5e7eb',
    border: isDark ? '1.5px solid #4b5563' : '1.5px solid #d1d5db',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  }}>
    <User size={22} color={isDark ? '#d1d5db' : '#4b5563'} strokeWidth={2} />
  </div>
);

const ChatMessage = ({ message, colorMode, onContinueFromHistory, onFeedbackLike, onFeedbackDislike, onFeedbackComment, onSendMessage }) => {
  const isUser = message.sender === 'user';
  const isDark = colorMode === 'dark';

  const renderText = (text) => {
    if (!text) return text;
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} style={{ fontWeight: 700 }}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  return (
    <div style={{
      padding: '10px 24px',
      display: 'flex',
      justifyContent: isUser ? 'flex-end' : 'flex-start',
    }}>
      {isUser ? (
        /* User: right-aligned blue bubble with avatar */
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', maxWidth: '75%' }}>
          <div style={{ flex: 1, textAlign: 'right' }}>
            <div style={{
              display: 'inline-block', textAlign: 'left',
              padding: '8px 14px', borderRadius: '18px 4px 18px 4px',
              background: isDark ? '#1e3a5f' : '#002677',
              color: '#fff',
              fontSize: '15px', lineHeight: '1.6', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
              {renderText(message.text)}
            </div>
            {message.timestamp && (
              <div style={{ fontSize: '11px', marginTop: '3px', color: isDark ? '#6b7280' : '#b0b0b0' }}>
                {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
          </div>
          <UserAvatar isDark={isDark} />
        </div>
      ) : (
        /* AI: left-aligned with avatar + card */
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', maxWidth: '80%' }}>
          <BotAvatar />
          <div style={{
            minWidth: 0, flex: 1,
            background: isDark ? '#1e1e2e' : '#fafbff',
            border: isDark ? '1px solid #2d2d3f' : '1px solid #e2e4f0',
            borderRadius: '18px 4px 18px 4px',
            padding: '16px 20px',
            boxShadow: isDark
              ? '0 3px 10px rgba(0,0,0,0.25), 0 1px 3px rgba(0,0,0,0.15)'
              : '0 2px 10px rgba(99,102,241,0.06), 0 1px 4px rgba(0,0,0,0.04)',
          }}>
            {/* <div style={{
              fontSize: '11px', fontWeight: 600, marginBottom: '6px',
              color: isDark ? '#a78bfa' : '#6366f1',
              letterSpacing: '0.3px',
            }}>RateLoader</div> */}
            <div style={{
              fontSize: '15.5px', lineHeight: '1.65', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              color: isDark ? '#e5e7eb' : '#1f2937',
            }}>
              {renderText(message.text)}
            </div>
            {message.metadata?.continueJobNumber && onContinueFromHistory && (
              <button
                onClick={() => onContinueFromHistory(message.metadata.continueJobNumber)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  marginTop: '12px', padding: '10px 20px',
                  border: 'none', borderRadius: '10px',
                  background: 'linear-gradient(135deg, #002677, #1a4fad)',
                  color: '#fff', cursor: 'pointer',
                  fontSize: '14px', fontWeight: 600,
                  boxShadow: '0 2px 8px rgba(0, 38, 119, 0.35)',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                  letterSpacing: '0.02em',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 38, 119, 0.45)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 38, 119, 0.35)'; }}
              >
                <Play size={15} />
                <span>Continue Job {message.metadata.continueJobNumber}</span>
              </button>
            )}
            {message.metadata?.suggestions?.length > 0 && onSendMessage && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                {message.metadata.suggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => onSendMessage(suggestion)}
                    style={{
                      padding: '7px 18px',
                      borderRadius: '20px',
                      border: isDark ? '1.5px solid #3b82f6' : '1.5px solid #002677',
                      background: isDark ? 'transparent' : '#f0f4ff',
                      color: isDark ? '#60a5fa' : '#002677',
                      fontSize: '13.5px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'background 0.15s, color 0.15s, transform 0.1s',
                      letterSpacing: '0.01em',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = isDark ? '#1e3a5f' : '#002677';
                      e.currentTarget.style.color = '#fff';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = isDark ? 'transparent' : '#f0f4ff';
                      e.currentTarget.style.color = isDark ? '#60a5fa' : '#002677';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
            {/* Feedback buttons — only on API-response status messages, never on history */}
            {message.metadata?.isApiResponse && !message.metadata?.isHistory && !message.metadata?.excludeFeedback && onFeedbackLike && (
              <FeedbackButtons
                messageId={message.id}
                reaction={message.metadata?.reaction}
                hasComment={!!message.metadata?.comment}
                onLike={onFeedbackLike}
                onDislike={onFeedbackDislike}
                onComment={onFeedbackComment}
                isDark={isDark}
              />
            )}
            {message.timestamp && (
              <div style={{ fontSize: '11px', marginTop: '6px', color: isDark ? '#6b7280' : '#b0b0b0' }}>
                {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatMessage;
